// src/components/Notice.js
/**
 * Inline notice — the dashboard house style for info / success / error
 * messages: a small coloured dot followed by text.
 *
 * Use this for ALL notices. Do NOT use red-background alert boxes
 * (`bg-red-100 border border-red-400 …`) — they are off-brand.
 *
 * Pass `role: "alert"` for error/validation notices so they remain accessible
 * (and assertable in tests).
 */

import React from "react";

/**
 * Renders an inline notice (coloured dot + message), or null when empty.
 *
 * @param {string} message - The notice text. Falsy → renders nothing.
 * @param {"neutral"|"success"|"error"} [tone="neutral"] - Dot colour.
 * @param {{ basisFull?: boolean, role?: string }} [options]
 * @returns {JSX.Element|null}
 */
export function renderNotice(message, tone = "neutral", options = {}) {
  if (!message) {
    return null;
  }

  const { basisFull = false, role } = options;
  const dotClassName =
    tone === "error"
      ? "bg-red-700"
      : tone === "success"
        ? "bg-green-700"
        : "bg-greyscale-3";

  return (
    <div
      className={`flex flex-row gap-2 items-center justify-start text-typography ${
        basisFull ? "md:basis-full" : ""
      }`}
    >
      <span className={`w-3 h-3 rounded-full ${dotClassName}`}></span>
      <p {...(role ? { role } : {})}>{message}</p>
    </div>
  );
}

/**
 * Component form of {@link renderNotice} for JSX usage, e.g.
 * `<Notice tone="error" role="alert">{message}</Notice>`.
 */
export default function Notice({
  message,
  children,
  tone = "neutral",
  basisFull = false,
  role,
}) {
  return renderNotice(message ?? children, tone, { basisFull, role });
}
